import { useState } from 'react'
import { shellControlClass } from "./shellControl";
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowDown01Icon, Tick02Icon } from "@hugeicons/core-free-icons"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext"
import { cn } from "@/lib/utils"

export default function WorkspaceSiteSelector() {
  const [open, setOpen] = useState(false)
  const workspaceContext = useWorkspaceSafe()

  if (!workspaceContext) return null

  const {
    enabledProperties,
    connectors,
    selectedPropertyIds,
    togglePropertySelection,
    workspace,
    isAggregateMode,
  } = workspaceContext

  const getConnectorInfo = (propertyId: string) => {
    const connector = connectors.find(c =>
      c.propertyId === propertyId ||
      c.metadata?.propertyId === propertyId ||
      c.metadata?.propertyIds?.includes(propertyId) ||
      c.sites?.some((s: any) => s.propertyId === propertyId)
    )
    return { type: connector?.type || 'humblytics', status: connector?.status || 'connected' }
  }

  // Deduplicate by propertyId (API may return duplicates)
  const seenIds = new Set<string>()
  const displayItems = enabledProperties
    .filter(p => {
      if (seenIds.has(p.propertyId)) return false
      seenIds.add(p.propertyId)
      return true
    })
    .map(p => {
      const connectorInfo = getConnectorInfo(p.propertyId)
      return {
        id: p.propertyId,
        propertyId: p.propertyId,
        domain: p.domain || '',
        name: p.name || p.domain || 'Untitled site',
        connectorType: connectorInfo.type,
        status: connectorInfo.status,
      }
    })

  if (displayItems.length === 0) return null

  // Pin selected items to the top
  const selectedSet = new Set(selectedPropertyIds)
  const sortedItems = [
    ...displayItems.filter(i => selectedSet.has(i.propertyId)),
    ...displayItems.filter(i => !selectedSet.has(i.propertyId)),
  ]

  // What to show in the trigger
  const triggerItem = !isAggregateMode && selectedPropertyIds.length === 1
    ? displayItems.find(i => i.propertyId === selectedPropertyIds[0])
    : null

  // Non-interactive display for single item workspaces
  if (displayItems.length === 1 && triggerItem) {
    return (
      <div className={cn(shellControlClass, "hover:bg-transparent")}>
        <span className="truncate max-w-[160px]">
          {triggerItem.domain || triggerItem.name}
        </span>
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(shellControlClass, "justify-between")}
        >
          <span className="truncate max-w-[160px]">
            {triggerItem
              ? (triggerItem.domain || triggerItem.name)
              : `${selectedPropertyIds.length} sites`}
          </span>
          <HugeiconsIcon icon={ArrowDown01Icon} size={13} className={cn(
            "text-muted-foreground/40 shrink-0 transition-transform duration-200",
            open && "rotate-180"
          )} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0"
        align="start"
        sideOffset={6}
      >
        <Command className="bg-transparent">
          <CommandList className="max-h-[380px]">
            <CommandEmpty className="py-6 text-xs text-muted-foreground">
              No sites in this workspace yet.
            </CommandEmpty>

            {/* Workspace header */}
            <div className="flex flex-col gap-0.5 px-3 py-2.5 border-b border-border">
              <p className="text-[13px] font-medium text-foreground truncate leading-tight">
                {workspace?.name || 'Untitled workspace'}
              </p>
              <p className="text-xs text-muted-foreground leading-tight">
                {selectedPropertyIds.length > 1
                  ? `${selectedPropertyIds.length} sites selected`
                  : 'Current workspace'}
              </p>
            </div>

            {/* Sites label */}
            <div className="px-3 pt-2.5 pb-1">
              <span className="text-xs font-medium text-muted-foreground">
                Sites
              </span>
            </div>

            {/* Site list */}
            <CommandGroup className="px-1.5 pb-1.5">
              {sortedItems.map((item) => {
                const isSelected = selectedSet.has(item.propertyId)
                return (
                  <CommandItem
                    key={item.id}
                    value={`${item.name} ${item.domain}`}
                    onSelect={() => togglePropertySelection(item.propertyId)}
                    className={cn(
                      "min-h-8 cursor-pointer py-1.5 px-2.5 rounded-lg transition-colors duration-100 flex items-center justify-between",
                      isSelected
                        ? "bg-muted/50 dark:bg-muted/30"
                        : "hover:bg-muted/40 dark:hover:bg-muted/20"
                    )}
                  >
                    <span className={cn(
                      "text-[13px] truncate leading-tight",
                      isSelected ? "font-medium text-foreground" : "text-foreground/70"
                    )}>
                      {item.domain || item.name || 'Untitled'}
                    </span>
                    {isSelected && (
                      <HugeiconsIcon icon={Tick02Icon} size={14} className="text-foreground/50 shrink-0 ml-2" />
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
